import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import DemoPasswordModal from './DemoPasswordModal';
import { HelpModal } from '../ui/HelpModal';
import { HelpCircle } from 'lucide-react';

const LoginPage: React.FC = () => {
  const { 
    signIn, 
    isLoading, 
    authError, 
    isOidcAvailable, 
    signInDemoWithPassword,
    needsDemoPassword,
    setNeedsDemoPassword
  } = useAuth();
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const handleMicrosoftSignIn = async (): Promise<void> => {
    try {
      await signIn('msal');
    } catch (error) {
      console.error('Microsoft login failed:', error);
    }
  };

  const handleOidcSignIn = async (): Promise<void> => {
    try {
      await signIn('oidc');
    } catch (error) {
      console.error('OIDC login failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rm-bg-0 via-rm-bg-0 to-rm-primary-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-rm-primary-100 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-rm-accent-100 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        <Card className="shadow-2xl border-0 overflow-hidden">
          {/* Decorative top border */}
          <div className="h-1.5 bg-gradient-to-r from-rm-primary-500 via-rm-primary-600 to-rm-accent-500"></div>
          
          <CardHeader className="text-center pt-12 pb-6 px-8">
            <div className="mb-6 inline-flex items-center justify-center mx-auto">
              <img 
                src="/Kim.png" 
                alt="Kim - Your Wellbeing Buddy" 
                className="w-32 h-32 rounded-full shadow-lg ring-4 ring-rm-primary-100"
              />
            </div>
            <CardTitle className="text-4xl font-bold text-rm-primary-700 leading-tight">
              Welcome to your<br/>
              <span className="text-rm-accent-600 font-extrabold">
                Wellbeing Buddy
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-8 px-8 pb-12">
            <p className="text-center text-base text-rm-neutral-600 leading-relaxed">
              Sign in with your account to chat with Kim 
            </p>

            <div className="text-center">
              <button
                onClick={() => setIsHelpModalOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm text-rm-primary-600 hover:text-rm-primary-700 font-medium transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                About Kim
              </button>
            </div>
            
            {authError && (
              <div className="p-4 text-red-700 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <svg className="w-5 h-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">{authError}</span>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {/* Renewmyday / OIDC Sign In (primary option, only show if enabled) */}
              {isOidcAvailable && (
                <>
                  <Button
                    onClick={handleOidcSignIn}
                    disabled={isLoading}
                    variant="primary"
                    size="lg"
                    className="w-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      'Sign in with Renewmyday'
                    )}
                  </Button>

                  {/* <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-rm-neutral-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-rm-neutral-500">or</span>
                    </div>
                  </div> */}
                </>
              )}

              {/* Microsoft / Azure AD Sign In - only when OIDC is not available */}
              {!isOidcAvailable && (
                <Button
                  onClick={handleMicrosoftSignIn}
                  disabled={isLoading}
                  variant="primary"
                  size="lg"
                  className="w-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Signing in...
                    </span>
                  ) : (
                    'Sign in with Microsoft'
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Bottom Center - Alternative Sign-in */}
      {isOidcAvailable && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 opacity-0 hover:opacity-100 transition-opacity duration-300">
          {/* Alternative Sign-in- only when OIDC is primary */}
          <button
            onClick={handleMicrosoftSignIn}
            disabled={isLoading}
            className="px-4 py-2.5 bg-white/80 backdrop-blur-sm text-rm-neutral-700 hover:text-rm-primary-700 hover:bg-white rounded-full shadow-md hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rm-accent-500 disabled:opacity-50 border border-rm-neutral-200"
            title="Sign in with Microsoft account"
          >
            <span className="text-sm font-medium">{isLoading ? 'Signing in...' : 'Alternative Sign-in'}</span>
          </button>
        </div>
      )}

      {/* Demo Password Modal */}
      <DemoPasswordModal
        isOpen={needsDemoPassword}
        onClose={() => setNeedsDemoPassword(false)}
        onSubmit={signInDemoWithPassword}
      />

      {/* Help / About Modal */}
      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </div>
  );
};

export default LoginPage;
